import { useTitle } from 'ahooks';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    COOKIE_USER_TOKEN,
    DEFAULT_MODEL_ID,
    SESSION_AI_CHAT_AUTO_SUBMIT,
    SESSION_AI_CHAT_INPUT,
    STORAGE_USER_INFO,
} from '@/scripts/const.ts';
import { useEffect, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { IChatModelDetail, ILogin } from '@/types/.fetch.ts';
import useAjax from '@/hooks/useAjax.ts';
import { genPath } from '@/scripts/utils.tsx';
import AiChatBubble from '@/pages/ai-chat/components/AiChatBubble.tsx';
import classnames from 'classnames';
import DefaultLayout from '@/components/layout/DefaultLayout.tsx';
import { PATH_AI_CHAT } from '@/router/routes.ts';
import AiChatFooter from '@/pages/ai-chat/components/AiChatFooter.tsx';
import * as localforage from 'localforage';
import Cookies from 'js-cookie';

export default function AiChatEntry() {
    useTitle('Ai智答');
    const navigate = useNavigate();
    const [search] = useSearchParams();
    const querySessionId = search.get('sessionId');

    const scrollArea = useRef<Scrollbars>(null);
    const [expended, setExpended] = useState(false);

    useEffect(() => {
        initUser();
    }, []);

    // 获取用户信息
    const { fetch: getUserInfo } = useAjax<ILogin>(
        {
            url: '/app/user/reg',
        },
        {
            manual: true,
            onSuccess: (data) => {
                if (data) {
                    localforage.setItem(STORAGE_USER_INFO, data);
                    Cookies.set(COOKIE_USER_TOKEN, data.token);
                }
            },
        },
    );

    // 获取引导信息
    const { response: guideData } = useAjax<IChatModelDetail>({
        url: `/app/ai/model?id=${DEFAULT_MODEL_ID}`,
    });

    const initUser = async () => {
        const userInfo: ILogin | null = await localforage.getItem(STORAGE_USER_INFO);
        if (!userInfo) {
            getUserInfo();
        } else {
            Cookies.set(COOKIE_USER_TOKEN, userInfo.token);
        }
    };

    // 提交
    const handleSubmit = (inputValue: string) => {
        if (inputValue) {
            sessionStorage.setItem(SESSION_AI_CHAT_INPUT, inputValue);
            sessionStorage.setItem(SESSION_AI_CHAT_AUTO_SUBMIT, 'true');
            navigate(genPath(PATH_AI_CHAT, { sessionId: querySessionId }));
        }
    };

    const renderGuide = () => {
        if (!guideData || querySessionId) return null;
        const problems = guideData?.exampleProblem || [];
        const problemsVisible = expended ? problems : problems.slice(0, 3);
        return (
            <>
                <AiChatBubble avatar={`/assets/images/icon_aichat_${guideData?.modelAvatar}.jpg`}>
                    {guideData?.guideWords}
                </AiChatBubble>
                {problems.length > 0 ? (
                    <>
                        <div className="rounded-2xl rounded-bl-none bg-white p-4 shadow">
                            <div className="font-medium">你可以这样跟我提问</div>
                            <div className="mt-4 text-sm">
                                {problemsVisible?.map((item, index) => {
                                    return (
                                        <div
                                            className={classnames(
                                                'mb-2 border-0 border-b border-dashed border-[#BFBCF6] pb-2',
                                                {
                                                    '!mb-0 border-none !pb-0': index === problemsVisible?.length - 1,
                                                },
                                            )}
                                            onClick={() => handleSubmit(item.content)}
                                            key={item.exampleProblemId}
                                        >
                                            {item.content}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {!expended && problems.length > 3 ? (
                            <div className="text-center" onClick={() => setExpended(true)}>
                                <img src="/assets/images/chevron-down.svg" alt="" />
                            </div>
                        ) : null}
                    </>
                ) : null}
            </>
        );
    };

    return (
        <DefaultLayout
            className="bg-white bg-cover bg-no-repeat"
            style={{ backgroundImage: 'url(/assets/images/bg-aichat.jpg)' }}
        >
            <Scrollbars className="h-full scroll-smooth" ref={scrollArea}>
                <div className="flex w-full flex-col gap-7 px-4 py-5">{renderGuide()}</div>
            </Scrollbars>
            <AiChatFooter onSubmit={handleSubmit} />
        </DefaultLayout>
    );
}
